import { type FormEvent, type ReactElement, useEffect, useState } from "react";

import {
  APIError,
  api,
  type PublicComment,
  type PublicListing,
} from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { formatBytes } from "./Documents";

export function Explore(): ReactElement {
  const { status } = useAuth();
  const [listings, setListings] = useState<PublicListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .explore()
      .then((page) => {
        if (!active) return;
        setListings(page.items);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(publicActionMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="public-shell">
      <PublicChrome
        authenticated={status === "authenticated"}
        current="explore"
      />
      <main className="page page-explore">
        <header className="page-heading">
          <div>
            <p className="eyebrow">Public gallery</p>
            <h1>Explore published models</h1>
            <p>
              Browse FCStd listings teams have chosen to share. Cards are
              placeholders — VibeCAD does not generate previews.
            </p>
          </div>
        </header>
        {error && (
          <section className="empty-state error-state" role="alert">
            <p className="eyebrow">Unable to load</p>
            <h2>Explore is unavailable right now.</h2>
            <p>{error}</p>
          </section>
        )}
        {!error && loading && (
          <section
            className="explore-grid"
            aria-busy="true"
            aria-label="Loading public listings"
          >
            {[0, 1, 2].map((value) => (
              <div className="explore-card skeleton" key={value} />
            ))}
          </section>
        )}
        {!error && !loading && listings.length === 0 && (
          <section className="empty-state">
            <p className="eyebrow">Nothing published yet</p>
            <h2>No public listings are available.</h2>
            <p>
              When a team owner or admin publishes a version, it will appear
              here for anyone to download.
            </p>
          </section>
        )}
        {!error && !loading && listings.length > 0 && (
          <section className="explore-grid" aria-label="Public listings">
            {listings.map((listing) => (
              <a
                className="explore-card"
                href={`/m/${encodeURIComponent(listing.slug)}`}
                key={listing.slug}
              >
                <div className="explore-placeholder" aria-hidden="true">
                  ◇
                </div>
                <div>
                  <h2>{listing.title}</h2>
                  <p>{listing.summary || "Published FreeCAD document."}</p>
                </div>
                <dl className="document-meta">
                  <div>
                    <dt>Team</dt>
                    <dd>{listing.team_name}</dd>
                  </div>
                  <div>
                    <dt>Version</dt>
                    <dd>v{listing.version_sequence}</dd>
                  </div>
                  <div>
                    <dt>Likes</dt>
                    <dd>{listing.like_count}</dd>
                  </div>
                </dl>
                <span className="card-link">
                  Open listing <span aria-hidden="true">→</span>
                </span>
              </a>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

export function PublicListingPage({ slug }: { slug: string }): ReactElement {
  const { actor, status } = useAuth();
  const signedIn = status === "authenticated" && !!actor;
  const [listing, setListing] = useState<PublicListing | null>(null);
  const [comments, setComments] = useState<PublicComment[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentBody, setCommentBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setUnavailable(false);
    setError(null);
    Promise.all([api.publicListing(slug), api.publicComments(slug)])
      .then(([nextListing, commentPage]) => {
        if (!active) return;
        setListing(nextListing);
        setComments(commentPage.items);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof APIError && cause.code === "not_found") {
          setUnavailable(true);
          return;
        }
        setError(publicActionMessage(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  async function toggleLike(): Promise<void> {
    if (!listing || !signedIn) return;
    setBusy(true);
    setError(null);
    try {
      const updated = listing.liked_by_viewer
        ? await api.unlikePublicListing(slug)
        : await api.likePublicListing(slug);
      setListing(updated);
    } catch (cause) {
      setError(publicActionMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitComment(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    const body = commentBody.trim();
    if (!body || !signedIn) return;
    setBusy(true);
    setError(null);
    try {
      const comment = await api.createPublicComment(slug, body);
      setComments((current) => [comment, ...current]);
      setListing((current) =>
        current
          ? { ...current, comment_count: current.comment_count + 1 }
          : current,
      );
      setCommentBody("");
    } catch (cause) {
      setError(publicActionMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function removeComment(commentId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.deletePublicComment(slug, commentId);
      setComments((current) =>
        current.filter((comment) => comment.id !== commentId),
      );
      setListing((current) =>
        current
          ? {
              ...current,
              comment_count: Math.max(0, current.comment_count - 1),
            }
          : current,
      );
    } catch (cause) {
      setError(publicActionMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function downloadListing(): Promise<void> {
    if (!listing) return;
    setDownloadBusy(true);
    setError(null);
    try {
      await api.downloadPublicListing(
        slug,
        `${listing.title.replace(/[\\/\0]/g, "_")}-v${listing.version_sequence}.FCStd`,
      );
    } catch (cause) {
      if (cause instanceof APIError && cause.code === "not_found") {
        setUnavailable(true);
      } else {
        setError(publicActionMessage(cause));
      }
    } finally {
      setDownloadBusy(false);
    }
  }

  if (unavailable) return <ListingUnavailable />;
  if (loading) {
    return (
      <div className="public-shell">
        <PublicChrome authenticated={signedIn} current="listing" />
        <main className="page">
          <section
            className="detail-skeleton"
            aria-busy="true"
            aria-label="Loading public listing"
          />
        </main>
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="public-shell">
        <PublicChrome authenticated={signedIn} current="listing" />
        <main className="page">
          <section className="empty-state error-state" role="alert">
            <p className="eyebrow">Unable to load</p>
            <h1>This listing could not be opened.</h1>
            <p>{error}</p>
            <a className="button button-secondary" href="/explore">
              Back to Explore
            </a>
          </section>
        </main>
      </div>
    );
  }

  if (!listing) return <ListingUnavailable />;

  return (
    <div className="public-shell">
      <PublicChrome authenticated={signedIn} current="listing" />
      <main className="page detail-page public-listing-page">
        <div className="breadcrumbs">
          <a href="/explore">Explore</a>
          <span aria-hidden="true">/</span>
          <span>{listing.title}</span>
        </div>
        <header className="detail-heading">
          <div className="detail-title">
            <span className="detail-glyph" aria-hidden="true">
              ◇
            </span>
            <div>
              <p className="eyebrow">{listing.team_name}</p>
              <h1>{listing.title}</h1>
              <p>
                Published by {listing.author.display_name} (@
                {listing.author.handle})
              </p>
            </div>
          </div>
          <button
            className="button button-primary"
            disabled={downloadBusy}
            onClick={() => void downloadListing()}
            type="button"
          >
            {downloadBusy ? "Downloading…" : "Download FCStd"}
          </button>
        </header>
        {error && (
          <section className="alert alert-danger" role="alert">
            {error}
          </section>
        )}
        <section className="listing-summary">
          <p>{listing.summary || "No summary was provided."}</p>
          <dl className="metric-strip" aria-label="Listing summary">
            <div>
              <span>Version</span>
              <strong>v{listing.version_sequence}</strong>
            </div>
            <div>
              <span>Size</span>
              <strong>{formatBytes(listing.size_bytes)}</strong>
            </div>
            <div>
              <span>License</span>
              <strong>{listing.license || "Unspecified"}</strong>
            </div>
            <div>
              <span>Published</span>
              <strong>{formatPublicDate(listing.published_at)}</strong>
            </div>
          </dl>
        </section>
        <section className="like-row">
          <strong>
            {listing.like_count} like{listing.like_count === 1 ? "" : "s"}
          </strong>
          {signedIn ? (
            <button
              className="button button-secondary"
              disabled={busy}
              onClick={() => void toggleLike()}
              type="button"
            >
              {listing.liked_by_viewer ? "Unlike" : "Like"}
            </button>
          ) : (
            <a className="button button-secondary" href="/signin">
              Sign in to like
            </a>
          )}
        </section>
        <section className="content-section" aria-labelledby="comments-heading">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Discussion</p>
              <h2 id="comments-heading">Comments</h2>
            </div>
            <span>{listing.comment_count} visible</span>
          </div>
          {signedIn ? (
            <form
              className="comment-composer"
              onSubmit={(event) => void submitComment(event)}
            >
              <label htmlFor="comment-body">Plaintext comment</label>
              <textarea
                id="comment-body"
                maxLength={2000}
                minLength={1}
                onChange={(event) => setCommentBody(event.target.value)}
                placeholder="Share feedback without HTML or markup."
                required
                rows={4}
                value={commentBody}
              />
              <small>{commentBody.trim().length}/2000</small>
              <button
                className="button button-primary"
                disabled={busy || commentBody.trim().length === 0}
                type="submit"
              >
                Post comment
              </button>
            </form>
          ) : (
            <p>
              Anyone can read comments. <a href="/signin">Sign in</a> to post
              one.
            </p>
          )}
          {comments.length === 0 ? (
            <div className="inline-empty">No comments yet.</div>
          ) : (
            <ul className="comment-list">
              {comments.map((comment) => (
                <li key={comment.id}>
                  <div>
                    <strong>{comment.author.display_name}</strong>
                    <small>@{comment.author.handle}</small>
                    <p>{comment.body}</p>
                    <time dateTime={comment.created_at}>
                      {formatPublicDate(comment.created_at)}
                    </time>
                  </div>
                  {actor?.handle === comment.author.handle && (
                    <button
                      className="button button-quiet"
                      disabled={busy}
                      onClick={() => void removeComment(comment.id)}
                      type="button"
                    >
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

export function ListingUnavailable(): ReactElement {
  return (
    <div className="public-shell">
      <PublicChrome authenticated={false} current="listing" />
      <main className="page">
        <section className="empty-state error-state">
          <p className="eyebrow">Unavailable</p>
          <h1>This listing is unavailable.</h1>
          <p>
            It may have been unpublished, never published, or the link may be
            incorrect.
          </p>
          <a className="button button-secondary" href="/explore">
            Back to Explore
          </a>
        </section>
      </main>
    </div>
  );
}

export function PublicChrome({
  authenticated,
  current,
}: {
  authenticated: boolean;
  current: "explore" | "listing";
}): ReactElement {
  return (
    <header className="topbar public-topbar">
      <a className="wordmark" href="/" aria-label="VibeCAD home">
        <span>VibeCAD</span>
      </a>
      <nav aria-label="Public navigation">
        <a className={current === "explore" ? "active" : ""} href="/explore">
          Explore
        </a>
        <a href={authenticated ? "/workspace" : "/signin"}>
          {authenticated ? "Workspace" : "Sign in"}
        </a>
      </nav>
    </header>
  );
}

export function publicListingSlug(pathname: string): string | null {
  const match = pathname.match(/^\/m\/([^/]+)$/);
  if (!match) return null;
  try {
    const slug = decodeURIComponent(match[1]).toLowerCase();
    if (
      slug.length < 3 ||
      slug.length > 64 ||
      slug.startsWith("-") ||
      slug.endsWith("-")
    ) {
      return null;
    }
    return /^[a-z0-9-]+$/.test(slug) ? slug : null;
  } catch {
    return null;
  }
}

export function publicActionMessage(cause: unknown): string {
  if (cause instanceof APIError) {
    if (cause.code === "not_found") {
      return "This listing is unavailable.";
    }
    if (cause.code === "validation_failed") {
      return "Comments must be 1–2000 characters of plaintext with no HTML.";
    }
    if (cause.code === "authentication_required") {
      return "Sign in to like or comment.";
    }
    return `${cause.message} · Request ${cause.requestId}`;
  }
  return cause instanceof Error
    ? cause.message
    : "The request could not be completed.";
}

function formatPublicDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
